import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsStorage } from '@next/core-lib/settings';
// @ts-ignore Ignore unused commented imports
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  skipWhile,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { PoultryContextKeys } from 'src/app/shared/constants';
import { SettingsServiceHelper } from 'src/app/shared/services/settingsHelperService';

import * as feedAdviceActions from '../../../feed-advice/store/feed-advice.actions';
import { Product } from '../../../shared/models/shared.model';
import { ProductModuleState } from '../../models/product.model';
import {
  ProductIsInUseResponse,
  UpsertProductResponse,
} from '../../services/product-service.model';
import { ProductService } from '../../services/product.service';
// @ts-ignore Ignore unused commented ActionTypes import
import * as actions from '../actions/product.actions';
import { getSelectedCountry } from '../selectors/product.selectors';

@Injectable()
export class ProductEffects {
  private readonly actions$ = inject(Actions);
  private ngZone = inject(NgZone);
  private router = inject(Router);
  private readonly store = inject<Store<ProductModuleState>>(Store);
  private localeService = inject(LocaleService);
  private productService = inject(ProductService);
  private dialogService = inject(DialogService);
  private settingsService = inject(SettingsServiceHelper);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadData),
      exhaustMap(() =>
        of(
          actions.clearProductState(),
          actions.loadSelectedCountryRequest(),
          actions.loadProductsRequest(),
          feedAdviceActions.loadFeedAdvicesRequest(),
        ),
      ),
    ),
  );

  loadProducts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadProductsRequest),
      switchMap(() =>
        combineLatest([this.store.select(getSelectedCountry)]).pipe(
          skipWhile((selectedCountry) => selectedCountry[0] === undefined),
          take(1),
        ),
      ),
      exhaustMap((_) =>
        this.productService.getProducts().pipe(
          switchMap((products: Product[]) => of(actions.loadProductsSuccess({ products }))),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.loadProductsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadSelectedCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadSelectedCountryRequest),
      exhaustMap((_) =>
        this.settingsService
          .getSingle('BE', PoultryContextKeys.PRODUCTS_COUNTRY, SettingsStorage.Global, true)
          .pipe(
            switchMap((settings) => of(actions.loadSelectedCountrySuccess({ id: settings }))),
            catchError((error: HttpErrorResponse) =>
              of(
                actions.loadProductsError({ error }),
                NotificationActions.errorSnackbar({ message: error.message }),
              ),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  setSelectedCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setSelectedCountryRequest),
      exhaustMap((id) =>
        this.productService.getProducts().pipe(
          switchMap((products: Product[]) => {
            this.settingsService.setSettings(
              PoultryContextKeys.PRODUCTS_COUNTRY,
              id.id,
              SettingsStorage.Global,
              true,
            );

            return of(
              actions.setSelectedCountrySuccess({
                id: id.id,
                products: products.filter((x) => x?.country === id.id),
              }),
            );
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.loadProductsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  setSelectedProduct$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setSelectedProductRequest),
      exhaustMap(({ id }) =>
        this.productService.getProductById(id).pipe(
          switchMap((product: Product) => of(actions.setSelectedProductSuccess({ product }))),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.setSelectedProductError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
        ),
      ),
    ),
  );

  getProducts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getProductsRequest),
      mergeMap(() =>
        this.productService.getProducts().pipe(
          switchMap((products: Product[]) => of(actions.getProductsSuccess({ products }))),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.getProductsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertProduct$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.upsertProductRequest),
      mergeMap((request) =>
        this.productService.upsertProduct(request.request).pipe(
          switchMap((response: UpsertProductResponse) =>
            of(
              actions.upsertProductSuccess({ response }),
              actions.loadProductsRequest(),
              NotificationActions.successSnackbar({
                message: this.localeService.translate('product-saved'),
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.upsertProductError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertProductSuccess$: Observable<UpsertProductResponse> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.upsertProductSuccess),
        map((action) => action.response),
        tap((_) => {
          this.ngZone.run(() => this.router.navigate(['/product'])).then();
        }),
      ),
    { dispatch: false },
  );

  deleteProductWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteProductWithConfirmation),
      exhaustMap(({ productId }) =>
        combineLatest([this.productService.checkProductIsInUse(productId), of(productId)]).pipe(
          take(1),
        ),
      ),
      switchMap(([response, productId]) =>
        this.dialogService
          .confirmWarning({
            title: this.localeService.translate('product-overview.delete-confirmation-title'),
            text: (response as ProductIsInUseResponse).isInUse
              ? this.localeService.translate(
                  `product-overview.delete-confirmation-message-when-in-use`,
                )
              : this.localeService.translate(`product-overview.delete-confirmation-message`),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  actions.deleteProductRequest({
                    productId,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteProductRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteProductRequest),
      mergeMap(({ productId }) =>
        this.productService.deleteProduct(productId).pipe(
          map(() => actions.deleteProductSuccess({ productId })),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.deleteProductFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteProductSucces$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.deleteProductSuccess),
        tap(() => {
          this.ngZone.run(() => this.router.navigate(['/product'])).then();
        }),
      ),
    { dispatch: false },
  );
}
